import Uploady, {
  useItemErrorListener,
  useItemFinishListener,
  useItemProgressListener,
  useItemStartListener,
} from '@rpldy/uploady';
import UploadDropZone from '@rpldy/upload-drop-zone';
import UploadButton from '@rpldy/upload-button';
import {
  Box,
  FormHelperText,
  LinearProgress,
  Link,
  styled,
} from '@mui/material';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { isNullOrEmpty } from '@util/string-util';
import { FormatServerResponseMethod } from '@rpldy/shared';
import Column from '@components/layout-util-components/column';
import { Gap } from '@components/layout-util-components/gap';
import Row from '@components/layout-util-components/row';
import { FlatButton } from '@components/buttons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/pro-light-svg-icons';

interface Props {
  value: number;
  onChange: (value?: number) => void;
  uploadUrl: string;
  previewUrl?: string;
  error?: string;
  formDataPropertyName?: string;
  requestMethod?: string;
  uploadMessage?: string;
  extraControls?: ReactNode;
}

const StyledDropZone = styled(UploadDropZone)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 10,
  padding: 10,
  border: '1px dashed #00000040',
  borderRadius: 5,
  fontFamily: 'Roboto',
  fontSize: '14px',
  fontWeight: '400',
  lineHeight: '20px',
  letterSpacing: '0.17000000178813934px',
  textAlign: 'center',

  '&.drag-over': {
    border: `4px dashed ${theme.palette.primary.main}`,
    padding: 7,
  },
}));

const StyledUploadButton = styled(UploadButton)(({ theme: { palette } }) => ({
  border: `1px solid ${palette.common.grey90}`,
  background: 'white',
  padding: '4px 10px',
  borderRadius: 4,
  color: palette.common.grey90,
  fontFamily: 'Roboto',
  fontSize: '13px',
  fontWeight: '500',
  lineHeight: '22px',
  letterSpacing: '0.46000000834465027px',
  textAlign: 'left',
  cursor: 'pointer',

  '&:hover': {
    background: palette.common.grey90,
    color: 'white',
  },
}));

const TextUploadButton = styled(UploadButton)(({ theme: { palette } }) => ({
  border: '0',
  background: 'none',
  padding: '2px 2px',
  color: palette.primary.main,
  fontFamily: 'Roboto',
  fontSize: '13px',
  fontWeight: '500',
  lineHeight: '22px',
  letterSpacing: '0.46000000834465027px',
  textAlign: 'left',
  cursor: 'pointer',

  '&:hover': {
    background: 'none',
    color: palette.primary.main,
    textDecoration: 'underline',
  },
}));

const TextCancelButton = styled(Link)(({ theme: { palette } }) => ({
  padding: '2px 2px',
  color: palette.common.grey90,
  fontFamily: 'Roboto',
  fontSize: '13px',
  fontWeight: '500',
  lineHeight: '22px',
  letterSpacing: '0.46000000834465027px',
  textAlign: 'left',
  cursor: 'pointer',
  textDecoration: 'none',

  '&:hover': {
    color: palette.common.grey90,
    textDecoration: 'underline',
  },
}));

const ItemsContainer = styled('div', { target: 'files-field-items-container' })(
  {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 8,

    '.item': {
      padding: 10,
      background: '#BABABA40',
      color: '#00000099',
      border: '1px solid #52525280',
      borderRadius: 4,
      fontFamily: 'Roboto',
      fontSize: '14px',
      fontWeight: '400',
      lineHeight: '21px',
      letterSpacing: '0.15000000596046448px',
      textAlign: 'left',
      '&.complete': {
        display: 'flex',
        alignItems: 'center',
      },
    },
  }
);

interface Uploads {
  [key: string]: {
    name: string;
    progress: number;
    size: string;
    error?: string;
  };
}

const UploadProgress = () => {
  const [uploads, setUploads] = useState<Uploads>({});

  useItemStartListener((item) => {
    // get file size in MB
    const fileSize = item.file.size / (1024 * 1024);
    setUploads((v) => ({
      ...v,
      [item.id]: {
        name: item.file.name,
        progress: 0,
        size: `${fileSize.toFixed(1)}mb`,
      },
    }));
  });

  useItemProgressListener((item) => {
    setUploads((v) => ({
      ...v,
      [item.id]: {
        ...v[item.id],
        progress: item.completed,
      },
    }));
  });

  useItemErrorListener((item) => {
    const errors = item.uploadResponse?.data?.errors;
    if (errors == null) {
      return;
    }

    const errorArray: Array<string> = Object.values(errors);
    if (Array.isArray(errorArray) && errorArray.length > 0) {
      const error = errorArray.join(', ');
      setUploads((v) => ({
        ...v,
        [item.id]: {
          ...v[item.id],
          error: error,
        },
      }));
    }
  });

  useItemFinishListener((item) => {
    setUploads((v) => {
      const newUploads = { ...v };
      delete newUploads[item.id];
      return newUploads;
    });
  });

  const entries = Object.entries(uploads);

  return (
    <ItemsContainer>
      {entries.map(([id, { name, progress, size, error }]) => {
        const hasError = !isNullOrEmpty(error);
        return (
          <div key={id} className="item">
            <Row>
              <Column sx={{ flexGrow: 1, flexShrink: 0 }}>
                <span>{`${name} (${size})`}</span>
                <LinearProgress variant="determinate" value={progress} />
                {hasError && <FormHelperText error>{error}</FormHelperText>}
              </Column>
              {hasError && (
                <>
                  <Gap size={12} />
                  <FlatButton
                    onClick={() => {
                      setUploads((v) => {
                        const newUploads = { ...v };
                        delete newUploads[id];
                        return newUploads;
                      });
                    }}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </FlatButton>
                </>
              )}
            </Row>
          </div>
        );
      })}
    </ItemsContainer>
  );
};

export default function ImageField({
  uploadUrl,
  previewUrl,
  value,
  onChange,
  error,
  formDataPropertyName = 'files',
  requestMethod = 'POST',
  uploadMessage = 'Drag files to upload',
  extraControls,
}: Props) {
  const valueRef = useRef<number | undefined>(value);

  const formatServerResponse: FormatServerResponseMethod = (
    response,
    status,
    _
  ) => {
    if (status === 200) {
      const json = JSON.parse(response);
      if (Array.isArray(json) && json.length === 1) {
        valueRef.current = json[0].fileMetadataId;
        onChange(valueRef.current ?? 0);
      }
    }
  };

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  // const updateFile = (file: BaseFile) => {
  //   onChange(
  //     valueRef.current.map((f) => {
  //       if (f.fileMetadataId === file.fileMetadataId) {
  //         return file;
  //       }
  //       return f;
  //     })
  //   );
  // };

  return (
    <Uploady
      withCredentials
      debug
      concurrent
      formatServerResponse={formatServerResponse}
      destination={{
        url: uploadUrl,
        method: requestMethod,
        filesParamName: formDataPropertyName,
      }}
    >
      <UploadProgress />
      <>
        {previewUrl && (
          <Row sx={{ alignItems: 'flex-start' }}>
            <img
              src={previewUrl}
              alt="Company Logo"
              style={{ maxHeight: '200px', maxWidth: '200px' }}
            />
            <Box>
              <Column sx={{ alignContent: 'flex-start', paddingLeft: '10px' }}>
                {extraControls}
                <TextUploadButton>Change</TextUploadButton>
                <TextCancelButton onClick={() => onChange(undefined)}>
                  Remove
                </TextCancelButton>
              </Column>
            </Box>
          </Row>
        )}
      </>
      <Gap size={12} />
      <>
        {!value && (
          <StyledDropZone onDragOverClassName="drag-over">
            <span>{uploadMessage}</span>
            <StyledUploadButton>Select file</StyledUploadButton>
          </StyledDropZone>
        )}
      </>
      <Column>
        {!isNullOrEmpty(error) ? (
          <>
            <Gap size={12} />
            <FormHelperText error>{error}</FormHelperText>
          </>
        ) : null}
      </Column>
    </Uploady>
  );
}
